import { useState, useCallback, useEffect } from "react";
import {useMatchChain} from "./index";
import {useQuery} from "@tanstack/react-query";
import {createPublicClient, defineChain, http} from "viem";
const CACHE_TTL = 86400 * 30 * 1000;
const MAX_CACHE_SIZE = 500;
const STORAGE_KEY = "match_receipt_logs";

type CacheEntry<V> = {
    value: V;
    timestamp: number;
};

export function useReceiptCache<K extends string, V>() {
    const [cache, setCache] = useState<Map<K, CacheEntry<V>>>(new Map());

    const isLocalStorageAvailable = (() => {
        try {
            const testKey = "__test__";
            localStorage.setItem(testKey, "1");
            localStorage.removeItem(testKey);
            return true;
        } catch (e) {
            return false;
        }
    })();

    useEffect(() => {
        if (isLocalStorageAvailable) {
            try {
                const storedData = localStorage.getItem(STORAGE_KEY);
                if (storedData) {
                    try {
                        const parsed = JSON.parse(storedData) as Record<K, CacheEntry<V>>;
                        const now = Date.now();

                        const validEntries: [K, CacheEntry<V>][] = Object.entries(parsed)
                            .filter(([_, entry]) => typeof entry === "object" && entry !== null && "timestamp" in entry && now - (entry as CacheEntry<V>).timestamp <= CACHE_TTL)
                            .map(([key, entry]) => [key as K, entry as CacheEntry<V>]);

                        setCache(new Map(validEntries));

                    } catch (e) {
                        console.error("Failed to parse cache from localStorage:", e);
                    }
                }

            } catch (e) {
                console.error("Failed to load cache from localStorage:", e);
            }
        }
    }, []);

    const updateLocalStorage = useCallback((updatedCache: Map<K, CacheEntry<V>>) => {
        if (isLocalStorageAvailable) {
            try {
                const storedData = localStorage.getItem(STORAGE_KEY);
                let parsed: Record<K, CacheEntry<V>> = {} as Record<K, CacheEntry<V>>;
                if (storedData) {
                    parsed = JSON.parse(storedData) as Record<K, CacheEntry<V>>;
                }

                updatedCache.forEach((entry, key) => {
                    parsed[key] = entry;
                });

                localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
            } catch (e) {
                console.error("Failed to update localStorage:", e);
            }
        }
    }, []);

    // set cache
    const set = useCallback((key: K, value: V) => {
        const now = Date.now();
        const newCache = new Map(cache);

        // clear expired
        newCache.forEach((entry, k) => {
            if (now - entry.timestamp > CACHE_TTL) {
                newCache.delete(k);
            }
        });

        // max cache size
        if (newCache.size >= MAX_CACHE_SIZE) {
            const firstKey = newCache.keys().next().value as K | undefined;
            if (firstKey !== undefined) {
                newCache.delete(firstKey);
            }
        }

        // set new value
        newCache.set(key, { value, timestamp: now });
        setCache(newCache);
        updateLocalStorage(newCache);
    }, [cache, updateLocalStorage]);

    // get cache
    const get = useCallback((key: K): V | undefined => {
        const entry = cache.get(key);
        if (entry) {
            // check if expired
            if (Date.now() - entry.timestamp > CACHE_TTL) {
                const newCache = new Map(cache);
                newCache.delete(key);
                setCache(newCache);
                updateLocalStorage(newCache);
                return undefined;
            }
            return entry.value;
        }
        return undefined;
    }, [cache, updateLocalStorage]);

    // del cache
    const del = useCallback((key: K) => {
        if (cache.has(key)) {
            const newCache = new Map(cache);
            newCache.delete(key);
            setCache(newCache);
            updateLocalStorage(newCache);
        }
    }, [cache, updateLocalStorage]);

    // clear cache
    const clear = useCallback(() => {
        setCache(new Map());
        if (isLocalStorageAvailable) {
            localStorage.removeItem(STORAGE_KEY);
        }
    }, [isLocalStorageAvailable]);

    return { set, get, del, clear };
}


export default function useReceipt({
    chainId,
    hash
                                   }:{
    chainId: number,
    hash: string
}){
    const {list} = useMatchChain()
    const cache = useReceiptCache<string, any>()
    const chain = list?.find((item) => item.id === chainId)
    const [shouldRefetch, setShouldRefetch] = useState(true)

    const query = useQuery({
        queryKey: ['match-tx-receipt', hash, chain],
        queryFn: async () => {
            if (!chain || !hash) return false
            const cacheKey = `${chain.id}-${hash}`
            if (cache.get(cacheKey)) {
                return cache.get(cacheKey)
            }
            try {
                const publicClient = createPublicClient({
                    chain: defineChain(chain),
                    transport: http(),
                });
                const receipt = await publicClient.getTransactionReceipt({hash: hash as `0x${string}`});
                if (!receipt) {
                    return false
                }
                cache.set(cacheKey, receipt)
                return receipt
            } catch (e) {
                return false
            }
        },
        refetchInterval: shouldRefetch ? 10000 : false,
    })

    useEffect(() => {
        if (query.data) {
            setShouldRefetch(false)
        }
    }, [query.data]);

    return query
}